/**
 * Area Command layer (R5 Slice 3, #507 — Ontario IMS §4.6 / App A #2). Renders each
 * OPEN Area Command parent incident (`is_area_command=true`, #493 spine) as an ambient
 * oversight marker at the parent's own geometry (set at establish, ST_MakePoint):
 *
 *   - CAHS `emergency-ops-centre` glyph in its ○ operations frame, ambient steel
 *     (assetStyles CAHS_ASSET_AMBIENT_INK #a7b0bd), STATIC — no motion of any kind;
 *     static means no animation, NOT stale: the data live-updates on the poll below.
 *   - An always-visible "Area Command" text label (AREA_COMMAND_HEADING) — the glyph is
 *     ALSO the OC disaster-response asset glyph (assetStyles OC_CATEGORY_CAHS_GLYPH),
 *     so the label is the disambiguator against that asset AND against a facility
 *     (never the word "post": no Area Command Post geometry renders here, App A #3).
 *   - A "+N incidents" badge (areaCommandIncidentsBadge — gate-corrected wording, App A
 *     #2/#35; NEVER "members") where N is the SEMANTIC count of the parent's live
 *     children (the detail children[] rollup — the same source the expand panel
 *     renders), never the screen clusterer.
 *
 * AMBIENT register: MapView installs this layer BEFORE the ACT-REGISTER block (#410),
 * so an urgent child marker always paints above the parent. Fetching is NOT gated on
 * visibility — the live Area Command count feeds the layer-picker row ("Area Command
 * (N)") even while the layer is off (the design gate's PEOC first-notice mitigation);
 * only rendering (`ds.show`) follows the toggle.
 *
 * Imperative installer (MapView owns the viewer), mirroring StagingAreasLayer:
 * refreshNow / setVisible / pick → the parent Incident → the incident expand panel.
 */
import * as Cesium from 'cesium';
import type { Incident } from '@tsr/shared-types';
import { fetchAreaCommands, fetchIncidentDetail } from '../api';
import { renderCahsFramedGlyph } from '../iconRegistry';
import {
  CAHS_ASSET_AMBIENT_ALPHA,
  CAHS_ASSET_AMBIENT_INK,
  CAHS_ASSET_FRAME_BACKING,
  CAHS_ASSET_FRAMED_SIZE,
} from '../assetStyles';
import { AREA_COMMAND_HEADING, areaCommandIncidentsBadge } from '../incidentVocabulary';
import type { AreaCommandIdentity } from '../areaCommandFirstNotice';

/** Re-poll cadence — matches the anomaly-lifecycle poll so membership changes made in
 *  the expand panel (place / remove) reflect on the badge without a camera nudge. */
export const AREA_COMMAND_POLL_MS = 45_000;

const AREA_COMMAND_PREFIX = 'areacmd:';
const AREA_COMMAND_BADGE_PREFIX = 'areacmd-badge:';

/** Marker label background — the dark label plate used by ambient layers. */
const LABEL_PLATE = '#0b1220';

/** An Area Command parent + the semantic count of its live child incidents. */
export interface AreaCommandMapItem {
  incident: Incident;
  /** `detail.children.length` — the #493 children[] rollup, the SAME source the
   *  expand panel renders, so badge N and panel N can never disagree. */
  childCount: number;
}

/** Default fetcher: open Area Commands + each parent's children[] count. N is tiny
 *  (open Area Commands), so the per-parent detail read is a handful of requests. */
export async function fetchAreaCommandMapItems(
  signal: AbortSignal,
): Promise<AreaCommandMapItem[]> {
  const parents = await fetchAreaCommands(signal);
  return Promise.all(
    parents.map(async (incident) => {
      const detail = await fetchIncidentDetail(incident.id, signal);
      return { incident, childCount: detail.children.length };
    }),
  );
}

export interface AreaCommandLayerOptions {
  /** Reports the live count of open Area Commands (the layer-picker row count) + their
   *  identities (id + name), so App can fire the #509 first-notice cue naming a newly-
   *  established Area Command on a genuine mid-session increase. */
  onCount?: (n: number, areaCommands: AreaCommandIdentity[]) => void;
  getVisible?: () => boolean;
  fetcher?: (signal: AbortSignal) => Promise<AreaCommandMapItem[]>;
  /** Test seam: disable the internal poll timer. */
  poll?: boolean;
}

export interface AreaCommandLayerHandle {
  refreshNow(): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): Incident | null;
  destroy(): void;
}

export function installAreaCommandLayer(
  viewer: Cesium.Viewer,
  opts: AreaCommandLayerOptions = {},
): AreaCommandLayerHandle {
  const fetcher = opts.fetcher ?? fetchAreaCommandMapItems;
  const ds = new Cesium.CustomDataSource('area-command');
  void viewer.dataSources.add(ds);
  // The framed CAHS glyph (○ operations frame baked from the glyph's own category
  // metadata) retinted to the ambient steel ink — the exact OC-asset treatment.
  const glyphUrl = renderCahsFramedGlyph('emergency-ops-centre', {
    backing: CAHS_ASSET_FRAME_BACKING,
    color: CAHS_ASSET_AMBIENT_INK,
  });

  const entityToIncident = new Map<Cesium.Entity, Incident>();
  let inflight: AbortController | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (items: AreaCommandMapItem[]): void => {
    ds.entities.removeAll();
    entityToIncident.clear();
    for (const { incident, childCount } of items) {
      const position = Cesium.Cartesian3.fromDegrees(incident.lon, incident.lat);
      const marker = ds.entities.add({
        id: `${AREA_COMMAND_PREFIX}${incident.id}`,
        position,
        billboard: glyphUrl
          ? {
              image: glyphUrl,
              width: CAHS_ASSET_FRAMED_SIZE,
              height: CAHS_ASSET_FRAMED_SIZE,
              // Ambient register: ≤0.85 α, constant — STATIC by construction (every
              // property below is a ConstantProperty; nothing pulses or animates).
              color: Cesium.Color.WHITE.withAlpha(CAHS_ASSET_AMBIENT_ALPHA),
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              verticalOrigin: Cesium.VerticalOrigin.CENTER,
            }
          : undefined,
        // Always-visible "Area Command" label — no distanceDisplayCondition, no
        // fade-to-zero: the disambiguator must read at every zoom the marker does.
        label: {
          text: AREA_COMMAND_HEADING,
          font: '11px sans-serif',
          fillColor: Cesium.Color.fromCssColorString(CAHS_ASSET_AMBIENT_INK),
          showBackground: true,
          backgroundColor: Cesium.Color.fromCssColorString(LABEL_PLATE).withAlpha(0.65),
          pixelOffset: new Cesium.Cartesian2(0, 14),
          verticalOrigin: Cesium.VerticalOrigin.TOP,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
      entityToIncident.set(marker, incident);
      // "+N incidents" badge — its own entity (one label per entity), offset
      // above-right, always visible wherever the marker is. Hidden at N=0 (nothing
      // to count; the label still marks the Area Command).
      if (childCount > 0) {
        const badge = ds.entities.add({
          id: `${AREA_COMMAND_BADGE_PREFIX}${incident.id}`,
          position,
          label: {
            text: areaCommandIncidentsBadge(childCount),
            font: '10px sans-serif',
            fillColor: Cesium.Color.fromCssColorString(CAHS_ASSET_AMBIENT_INK),
            showBackground: true,
            backgroundColor: Cesium.Color.fromCssColorString(LABEL_PLATE).withAlpha(0.65),
            pixelOffset: new Cesium.Cartesian2(14, -14),
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
        entityToIncident.set(badge, incident);
      }
    }
  };

  const refreshNow = async (): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const items = await fetcher(ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(items);
      opts.onCount?.(
        items.length,
        items.map(({ incident }) => ({ id: incident.id, name: incident.name })),
      );
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchAreaCommandMapItems failed', err);
    }
  };

  // Poll regardless of visibility (the count feeds the picker row while the layer is
  // off); the initial fetch seeds the first-notice count on boot.
  void refreshNow();
  const pollTimer =
    opts.poll === false ? null : setInterval(() => void refreshNow(), AREA_COMMAND_POLL_MS);

  return {
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): Incident | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToIncident.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (pollTimer) clearInterval(pollTimer);
      if (inflight) inflight.abort();
      entityToIncident.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
